import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import SectionCard from "@/components/customers/SectionCard";
import { cn } from "@/lib/utils";
import { formatDate, formatNOK } from "@/lib/format";
import type { FreelancerProjectHistoryRow } from "@/lib/db-types";

/**
 * VIB-2927/VIB-2934: prosjektene frilanseren er brukt på, i full bredde under
 * toppkortet. Rolle er linjene frilanseren er valgt på i Timetildeling, eller
 * linjene bilagene fra koblet leverandør er fordelt på. Est. kost vises per
 * linje og summeres aldri (en linje kan ha flere frilansere). Fakturert er
 * bilag fra koblede leverandører, et visningstall og ikke en KPI. En negativ
 * sum (kreditnotaer større enn fakturaer) vises som avvik.
 */
export function FreelancerProjectsCard({ rows }: { rows: FreelancerProjectHistoryRow[] }) {
  const last = rows[0]?.last_used;
  return (
    <SectionCard
      title="Prosjekter"
      meta={`${rows.length} prosjekt${rows.length === 1 ? "" : "er"}${last ? ` · siste ${formatDate(last)}` : ""}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-2 font-medium">Prosjekt</th>
              <th className="px-4 py-2 font-medium">Rolle</th>
              <th className="px-4 py-2 text-right font-medium">Est. kost på linjen</th>
              <th className="px-4 py-2 text-right font-medium">Fakturert</th>
              <th className="px-4 py-2 text-right font-medium">Bilag</th>
              <th className="px-4 py-2 font-medium">Siste</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.project_id} className="border-b border-border/50 align-top last:border-0">
                <td className="px-4 py-2.5">
                  <Link to={`/prosjekter/${r.project_id}`} className="font-medium text-primary hover:underline">
                    {[r.project_code, r.project_name].filter(Boolean).join(" ")}
                  </Link>
                  {(r.customer_name || r.production_lead_name) && (
                    <div className="text-xs text-muted-foreground">
                      {[r.customer_name, r.production_lead_name && `PL ${r.production_lead_name}`].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  {r.lines.length === 0
                    ? <span className="text-muted-foreground">—</span>
                    : r.lines.map((l) => <div key={l.budget_line_id}>{l.description ?? "Linje"}</div>)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {r.lines.length === 0
                    ? <span className="text-muted-foreground">—</span>
                    : r.lines.map((l) => <div key={l.budget_line_id}>{l.estimated_cost ? formatNOK(l.estimated_cost) : "—"}</div>)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums font-medium">
                  {r.voucher_count === 0 ? (
                    <span className="font-normal text-muted-foreground">—</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <span className={cn(r.invoiced < 0 && "text-destructive")}>{formatNOK(r.invoiced)}</span>
                      {r.invoiced < 0 && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 px-1.5 text-[11px] font-medium text-amber-900" title="Kreditnotaene er større enn fakturaene. Sjekk bilagene i PowerOffice.">
                          sjekk
                        </Badge>
                      )}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">{r.voucher_count || "—"}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{r.last_used ? formatDate(r.last_used) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
        Fakturert er godkjente og bokførte bilag fra koblede leverandører. Est. kost står per linje og summeres ikke.
      </p>
    </SectionCard>
  );
}

/** VIB-2934: én linje i sidekolonnen når frilanseren ikke er brukt på prosjekter ennå. */
export function FreelancerProjectsEmpty() {
  return <SectionCard title="Prosjekter" compact empty="Ikke brukt på prosjekter ennå." />;
}
